import React, { useState } from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './histogram.Renderer';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { TpRectangleSelection } from '../../zoompan-canvas/helpers';
import { ChartFilterButton, convertColumnDataToDisplayString } from '../../helpers/misc';
import styles from './histogram.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectionHighlightColor } from '../../../themes/themes';

function FilterValueEdit({ valueString, onModified }: { valueString: string; onModified: (newValue: number) => void }) {
  const [currentString, setCurrentString] = useState(valueString);
  const [isModified, setIsModified] = useState(false);

  function update() {
    const newStringValue = currentString;
    let newNumericalValue = NaN;
    if (newStringValue.length > 0) newNumericalValue = parseFloat(newStringValue);
    onModified(newNumericalValue);
    setIsModified(false);
  }

  return (
    <div
      className={styles.filterFieldWrapper}
      style={{
        background: isModified
          ? getSelectionHighlightColor().toCanvasString()
          : valueString.length > 0
            ? 'var(--highlightTextBg)'
            : undefined,
      }}
    >
      <input
        value={(isModified ? currentString : valueString) || ''}
        onChange={(ev) => {
          const newStringValue = ev.target.value;
          setIsModified(true);
          setCurrentString(newStringValue);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') update();
        }}
      />
      {isModified && (
        <div className={styles.filterFieldConfirmButton} onClick={(ev) => update()}>
          <FontAwesomeIcon icon="check" />
        </div>
      )}
    </div>
  );
}

export function Histogram({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const {
    definition,
    areaPoints,
    dataTable,
    dataValue,
    dataItemId,
    nameX,
    nameY,
    itemCount,
    colInfoValue,
    filterInfo,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config);

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    return null;
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    return null;
  }

  function onRectangleSelection(rectangleSelection: TpRectangleSelection) {
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.NUMERICALRANGE,
      dataTableId: dataTable.id,
      colId: colInfoValue.id,
      rangeMin: rectangleSelection.logicXMin,
      rangeMax: rectangleSelection.logicXMax,
    });
  }

  function onItemClicked(itemId: string) {}

  return (
    <>
      <div style={{ width: '100%', height: 'calc(100% - 40px)' }}>
        <ZoomPanCanvas
          definition={definition}
          getHoverInfo={getHoverInfo}
          getHoverRenderInfo={getHoverRenderInfo}
          onRectangleSelection={onRectangleSelection}
          onItemClicked={onItemClicked}
          widgetContext={widgetContext}
          maxZoomXRelative={30}
          maxZoomYRelative={30}
          chartTitleInfo={{
            assetId: dataContext.assetId,
            dataType: chartTypeManifest.id,
            title,
          }}
        />
        {filterInfo && <ChartFilterButton />}
      </div>
      <div style={{ width: '100%', height: '40px', paddingLeft: '10px', paddingTop: '1px', whiteSpace: 'nowrap' }}>
        Filter:&nbsp;
        <FilterValueEdit
          valueString={(filterInfo && convertColumnDataToDisplayString(colInfoValue, filterInfo.rangeMin)) || ''}
          onModified={(newValue: number) => {
            widgetContext.handleUserSelectionAction({
              type: TpEnSelectionFilterTypes.NUMERICALRANGE,
              dataTableId: dataTable.id,
              colId: colInfoValue.id,
              rangeMin: newValue,
              rangeMax: filterInfo ? filterInfo!.rangeMax : NaN,
            });
          }}
        />
        &nbsp;&mdash;&nbsp;
        <FilterValueEdit
          valueString={(filterInfo && convertColumnDataToDisplayString(colInfoValue, filterInfo.rangeMax)) || ''}
          onModified={(newValue: number) => {
            widgetContext.handleUserSelectionAction({
              type: TpEnSelectionFilterTypes.NUMERICALRANGE,
              dataTableId: dataTable.id,
              colId: colInfoValue.id,
              rangeMin: filterInfo ? filterInfo!.rangeMin : NaN,
              rangeMax: newValue,
            });
          }}
        />
      </div>
    </>
  );
}
